import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { Button, Spin, Tooltip } from 'antd';
import {
  CloseOutlined,
  InfoCircleOutlined,
  InstagramOutlined,
  TwitterOutlined,
} from '@ant-design/icons';
// Api
import { GET_BOOK_ME_STORES } from 'api/store/queries';
// Types
import { GetCustomCampaignDraftOffers_getCampaignOffers_entities } from 'api/campaignV2/types/GetCustomCampaignDraftOffers';
import {
  GetBookMeStores,
  GetBookMeStoresVariables,
} from 'api/store/types/GetBookMeStores';
import {
  AdminStoresOrderBy,
  SocialMediaType,
  StoreStatus,
  UserRole,
} from 'api/graphql-global-types';
// Helpers
import { formatAudienceSize } from 'helpers/campaignEditModal';
// Components
import SelectInfiniteScroll, {
  LoadSelectInfiniteScrollOptionsArgs,
} from 'components/common/SelectInfiniteScroll/SelectInfiniteScroll';
// Style
import styles from './PreselectedMultiTalentSection.module.scss';

const STORE_ROLES = [UserRole.Athlete, UserRole.ContentCreator];
const MIN_TALENTS = 2;

const SOCIAL_PLATFORMS: {
  type: SocialMediaType;
  label: string;
  icon: React.ReactNode;
}[] = [
  {
    type: SocialMediaType.Instagram,
    label: 'Instagram',
    icon: <InstagramOutlined className={styles.socialIcon} />,
  },
  {
    type: SocialMediaType.Tiktok,
    label: 'TikTok',
    icon: (
      <svg
        className={styles.socialIcon}
        viewBox="0 0 24 24"
        aria-hidden
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M16.6 5.82A4.66 4.66 0 0 1 14.8 3h-3.1v12.4a2.55 2.55 0 1 1-1.8-2.45V9.8a5.7 5.7 0 1 0 4.9 5.64V9.6a7.8 7.8 0 0 0 4.4 1.35V7.9a4.7 4.7 0 0 1-2.6-2.08Z"
        />
      </svg>
    ),
  },
  {
    type: SocialMediaType.Twitter,
    label: 'X',
    icon: <TwitterOutlined className={styles.socialIcon} />,
  },
];

export type PreselectedOffer =
  GetCustomCampaignDraftOffers_getCampaignOffers_entities;

type PreselectedMultiTalentSectionProps = {
  offers: PreselectedOffer[];
  loading: boolean;
  refetch: () => Promise<unknown>;
  onTalentIdsChange: (talentIds: string[]) => void;
  // Saves the whole campaign with the new roster. updateCustomCampaign replaces
  // every field it receives, so a talentIds-only call would wipe the brief.
  onPersistTalentIds: (talentIds: string[]) => Promise<boolean>;
};

const TALENT_TARGETING_TOOLTIP = (
  <div className={styles.tooltipContent}>
    <p className={styles.tooltipIntro}>
      Custom campaigns can target talent in two ways:
    </p>
    <p>
      <strong>My Talent</strong> — When a campaign is created from My Talent
      with selected athletes, targeting filters are skipped. Only the chosen
      talents (and any you add here) are invited.
    </p>
    <p>
      <strong>Filters</strong> — When a campaign is created normally, talent is
      matched with Talent &amp; Audience filters instead of a fixed list. Those
      filters appear when there are no preselected draft offers.
    </p>
  </div>
);

const getDisplayName = (offer: PreselectedOffer): string =>
  [offer.firstName, offer.lastName].filter(Boolean).join(' ').trim() ||
  offer.profileName?.trim() ||
  'Talent';

const getInitials = (offer: PreselectedOffer): string => {
  const first = offer.firstName?.trim().charAt(0) ?? '';
  const last = offer.lastName?.trim().charAt(0) ?? '';
  return `${first}${last}`.toUpperCase() || '?';
};

const getAvatarUrl = (offer: PreselectedOffer): string | null =>
  (offer.user?.avatarUrl ?? offer.avatarUrl)?.trim() || null;

const getMetaLabel = (offer: PreselectedOffer): string | null => {
  if (offer.slug?.trim()) return `@${offer.slug.trim()}`;
  const fullName = [offer.firstName, offer.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const profileName = offer.profileName?.trim();
  if (profileName && profileName !== fullName) return profileName;
  return null;
};

const getOfferSocialFollowers = (offer: PreselectedOffer) => {
  const socialMedia = offer.user?.socialMedia ?? [];

  return SOCIAL_PLATFORMS.flatMap(({ type, label, icon }) => {
    const account = socialMedia.find((item) => item.type === type);
    if (!account?.followingCnt) return [];

    return [
      {
        type,
        label,
        icon,
        count: formatAudienceSize(String(account.followingCnt), 1) || '—',
      },
    ];
  });
};

const getTotalFollowers = (offer: PreselectedOffer): string => {
  const socialTotal = (offer.user?.socialMedia ?? []).reduce(
    (sum, item) => sum + (item.followingCnt ?? 0),
    0
  );
  const total = socialTotal > 0 ? socialTotal : offer.socialFollowing ?? 0;
  return formatAudienceSize(String(total), 1) || '—';
};

const getStoreLabel = (store: {
  firstName: string;
  lastName: string;
  storeDetails?: { storeName?: string | null } | null;
}): string =>
  store.storeDetails?.storeName || `${store.firstName} ${store.lastName}`;

const SectionTitle = (): JSX.Element => (
  <h3 className={styles.sectionTitle}>
    <span>Talent</span>
    <Tooltip title={TALENT_TARGETING_TOOLTIP} placement="right">
      <button
        type="button"
        className={styles.infoButton}
        aria-label="How talent targeting works"
      >
        <InfoCircleOutlined className={styles.infoIcon} />
      </button>
    </Tooltip>
  </h3>
);

const PreselectedMultiTalentSection = ({
  offers,
  loading,
  refetch,
  onTalentIdsChange,
  onPersistTalentIds,
}: PreselectedMultiTalentSectionProps): JSX.Element => {
  const client = useApolloClient();
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [addingTalentId, setAddingTalentId] = useState<string | null>(null);
  const [addSelectKey, setAddSelectKey] = useState(0);
  const [brokenAvatarIds, setBrokenAvatarIds] = useState<Set<string>>(
    () => new Set()
  );
  const [updating, setUpdating] = useState(false);

  const talentIds = useMemo(
    () =>
      offers
        .map((offer) => offer.userId)
        .filter((id): id is string => Boolean(id)),
    [offers]
  );
  const talentIdsKey = talentIds.join(',');

  const selectedTalentIdSet = useMemo(() => new Set(talentIds), [talentIds]);

  useEffect(() => {
    onTalentIdsChange(talentIds);
    // Sync from the serialized id list so Apollo entity-array identity churn
    // does not re-trigger parent setState / entitlements queries.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentIdsKey]);

  const updateTalentIds = async (nextIds: string[]): Promise<boolean> => {
    setUpdating(true);
    try {
      const didPersist = await onPersistTalentIds(nextIds);
      if (didPersist) {
        await refetch();
      }
      return didPersist;
    } finally {
      setUpdating(false);
    }
  };

  const handleRemove = async (userId: string): Promise<void> => {
    if (talentIds.length <= MIN_TALENTS) return;

    const nextIds = talentIds.filter((id) => id !== userId);
    if (nextIds.length < MIN_TALENTS) return;

    setRemovingUserId(userId);
    try {
      await updateTalentIds(nextIds);
    } finally {
      setRemovingUserId(null);
    }
  };

  const handleAdd = async (userId: string): Promise<void> => {
    if (!userId || selectedTalentIdSet.has(userId)) return;

    setAddingTalentId(userId);
    try {
      const didUpdate = await updateTalentIds([...talentIds, userId]);
      if (didUpdate) {
        setAddSelectKey((key) => key + 1);
      }
    } finally {
      setAddingTalentId(null);
    }
  };

  const loadOptions = useCallback(
    async ({ search, offset, limit }: LoadSelectInfiniteScrollOptionsArgs) => {
      const { data } = await client.query<
        GetBookMeStores,
        GetBookMeStoresVariables
      >({
        query: GET_BOOK_ME_STORES,
        fetchPolicy: 'network-only',
        variables: {
          storeRoles: STORE_ROLES,
          input: {
            ...(search && { storeName: search }),
            orderBy: AdminStoresOrderBy.storeName,
            status: [StoreStatus.Active],
            isOnboarded: true,
            limit,
            offset,
          },
        },
      });

      const options = (data?.adminStores.entities ?? [])
        .filter((store) => !selectedTalentIdSet.has(store.id))
        .map((store) => ({
          value: store.id,
          label: getStoreLabel(store),
        }));

      return {
        options,
        total: data?.adminStores.total ?? 0,
      };
    },
    [client, selectedTalentIdSet]
  );

  const canRemove = talentIds.length > MIN_TALENTS;
  const count = offers.length;

  if (loading && !offers.length) {
    return (
      <section className={styles.section}>
        <SectionTitle />
        <div className={styles.loader}>
          <Spin />
        </div>
      </section>
    );
  }

  return (
    <section className={styles.section}>
      <SectionTitle />

      <p className={styles.header}>
        {count} talent selected for this campaign. At least {MIN_TALENTS} are
        required
        {canRemove ? " — remove anyone who shouldn't be included." : '.'}
      </p>

      <div className={styles.addWrapper}>
        <SelectInfiniteScroll
          key={addSelectKey}
          id="addPreselectedTalent"
          placeholder="Add talent"
          loadOptions={loadOptions}
          onSelect={handleAdd}
          disabled={updating || Boolean(addingTalentId)}
          allowClear
        />
      </div>

      <ul className={styles.list}>
        {offers.map((offer) => {
          const userId = offer.userId;
          const displayName = getDisplayName(offer);
          const metaLabel = getMetaLabel(offer);
          const avatarUrl = getAvatarUrl(offer);
          const showAvatar =
            Boolean(avatarUrl) && !brokenAvatarIds.has(offer.id);
          const isRemoving = Boolean(userId && removingUserId === userId);
          const socialFollowers = getOfferSocialFollowers(offer);
          const totalFollowers = getTotalFollowers(offer);

          return (
            <li key={offer.id} className={styles.row}>
              {showAvatar ? (
                <img
                  className={styles.avatar}
                  src={avatarUrl as string}
                  alt=""
                  width={40}
                  height={40}
                  onError={() =>
                    setBrokenAvatarIds((prev) => new Set(prev).add(offer.id))
                  }
                />
              ) : (
                <div className={styles.avatarFallback} aria-hidden>
                  {getInitials(offer)}
                </div>
              )}

              <div className={styles.identity}>
                <p className={styles.name}>{displayName}</p>
                {metaLabel ? <p className={styles.meta}>{metaLabel}</p> : null}
              </div>

              <div className={styles.audience}>
                {socialFollowers.length > 0 && (
                  <div className={styles.socials}>
                    {socialFollowers.map(({ type, label, icon, count }) => (
                      <span
                        key={type}
                        className={styles.socialItem}
                        aria-label={`${label} followers ${count}`}
                      >
                        {icon}
                        <span className={styles.socialCount}>{count}</span>
                      </span>
                    ))}
                  </div>
                )}
                <div
                  className={styles.total}
                  aria-label={`Total audience ${totalFollowers}`}
                >
                  <span className={styles.totalLabel}>Total</span>
                  <span className={styles.totalValue}>{totalFollowers}</span>
                </div>
              </div>

              {canRemove && (
                <Button
                  type="text"
                  className={styles.removeButton}
                  icon={<CloseOutlined />}
                  onClick={() => userId && handleRemove(userId)}
                  disabled={!userId || isRemoving || updating}
                  loading={isRemoving}
                  aria-label={`Remove ${displayName}`}
                />
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default PreselectedMultiTalentSection;
